/**
 * useFunzioniRouting — "questa funzione oggi avviene in Vue o nel portale classico?"
 *
 * Carica UNA volta `/api/v2/funzioni-routing` (catalogo globale, ~54 righe) e lo tiene in cache
 * per tutta la sessione della SPA, come fa `useLegacyLink`.
 *
 * Perché serve, visto che esiste già `useMenuNav`: quello cerca nell'albero del **menu**, che
 * contiene solo le voci visibili (`showHome=1`). Le funzioni-azione — un bottone dentro una
 * pagina, come "Nuovo Centro" (funzione 202) — non sono voci di menu, quindi lì non si trovano.
 * Senza questo catalogo un bottone con la route Vue cablata continua ad aprire Vue anche dopo che
 * la funzione è stata riportata a legacy dal pannello menu, rendendo il flag `is_legacy` bugiardo.
 *
 * Degrada in silenzio: se la chiamata fallisce il catalogo resta vuoto e `destinazione()` torna
 * null, cioè "comportati come prima" (route Vue). Meglio una pagina nuova che un bottone morto.
 */
import { ref } from 'vue'
import { apiClient } from '@/api/client'

export interface FunzioneRouting {
  idFunzione: number
  springRoute: string
  isLegacy: boolean
  pagina?: string | null
  /**
   * ⚠️ Non è sempre un percorso `.xhtml`: per le funzioni-azione contiene il nome dell'action
   * legacy (es. `nuovoCentro`), che non è raggiungibile per URL. Sempre validato con `isXhtml`.
   */
  paginaEdit?: string | null
}

let _fetch: Promise<void> | null = null
const _voci = ref<FunzioneRouting[]>([])

async function _load(): Promise<void> {
  try {
    const { data } = await apiClient.get<FunzioneRouting[]>('/api/v2/funzioni-routing')
    _voci.value = Array.isArray(data) ? data : []
  } catch {
    _voci.value = []
  }
}

function ensureLoaded(): Promise<void> {
  if (!_fetch) _fetch = _load()
  return _fetch
}

function isXhtml(p: string | null | undefined): p is string {
  return typeof p === 'string' && p.endsWith('.xhtml')
}

export function useFunzioniRouting() {
  ensureLoaded()

  /** La riga di catalogo per una route Vue, o null se la funzione non è nel catalogo. */
  function destinazione(springRoute: string): FunzioneRouting | null {
    return _voci.value.find((v) => v.springRoute === springRoute) ?? null
  }

  /**
   * True se quella route va servita dal portale classico. Se la funzione non è nel catalogo
   * (o il catalogo non si è caricato) risponde false: si resta sul comportamento Vue.
   */
  function isLegacy(springRoute: string): boolean {
    return destinazione(springRoute)?.isLegacy === true
  }

  /**
   * Pagina Seam da aprire per quella route, o null se la funzione non ne dichiara una
   * utilizzabile — capita per le funzioni-azione, che nel classico sono bottoni dentro una
   * pagina e non hanno un indirizzo proprio. In quel caso il chiamante deve indicare lui dove
   * mandare l'utente (tipicamente l'elenco da cui quel bottone si raggiunge).
   */
  function paginaLegacy(springRoute: string): string | null {
    const v = destinazione(springRoute)
    if (!v) return null
    if (isXhtml(v.pagina)) return v.pagina
    if (isXhtml(v.paginaEdit)) return v.paginaEdit
    return null
  }

  return { destinazione, isLegacy, paginaLegacy, voci: _voci }
}
